import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import HotelIcon from '@mui/icons-material/Hotel';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import moment from 'moment';
import DashboardHeader from '../components/common/DashboardHeader';
import BookingDocumentsPreviewPanel from '../components/booking/BookingDocumentsPreviewPanel';
import useMobileView from '../hooks/useMobileView';
import { getGroupBookingDetails } from '../services/api';
import { getBookingCurrencySymbol, getPaymentSummaryData } from '../utils/bookingPaymentSummary';
import {
  DASHBOARD_ACCENT,
  getDashboardPageShellSx,
} from '../utils/dashboardSurfaceSx';

const ACCENT = DASHBOARD_ACCENT || '#4ce1b6';

function formatDate(value) {
  if (!value) return '—';
  const m = moment(value);
  return m.isValid() ? m.format('DD MMM YYYY') : '—';
}

function formatMoney(amount, currency = 'INR') {
  const symbol = getBookingCurrencySymbol(currency);
  const n = Number(amount);
  if (!Number.isFinite(n)) return `${symbol}0.00`;
  return `${symbol}${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function resolveItemPrice(item) {
  const n = Number(item?.price ?? item?.total ?? item?.amount ?? 0);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Collapse per-night booking items into room-type + rate-plan rows.
 * Rooms = typical concurrent count for that combo; nights = unique dates; rate = unit nightly.
 */
function aggregateItemsByRoomTypeAndRatePlan(items = []) {
  const groups = new Map();

  items.forEach((item, index) => {
    const roomTypeId = item.room_type_id || item.room_type?.id || null;
    const ratePlanId = item.rate_plan_id || item.rate_plan?.id || null;
    const roomTypeName = item.room_type_name || item.room_type?.name || 'Room';
    const ratePlanName = item.rate_plan_name || item.rate_plan?.name || '—';
    const key = `${roomTypeId || roomTypeName}::${ratePlanId || ratePlanName}`;
    const dateKey = item.date || item.stay_date || '';
    const price = resolveItemPrice(item);
    const sequence = item.room_sequence ?? item.allocated_room_id ?? item.allocated_room?.id ?? index;

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        roomTypeName,
        ratePlanName,
        total: 0,
        dates: new Set(),
        roomsByDate: new Map(),
        sequences: new Set(),
        prices: [],
      });
    }

    const group = groups.get(key);
    group.total += price;
    group.prices.push(price);
    if (dateKey) group.dates.add(String(dateKey));
    group.sequences.add(String(sequence));
    if (dateKey) {
      const prev = group.roomsByDate.get(String(dateKey)) || 0;
      group.roomsByDate.set(String(dateKey), prev + 1);
    }
  });

  return Array.from(groups.values()).map((group) => {
    const nights = group.dates.size || 1;
    const roomsFromDates = group.roomsByDate.size
      ? Math.max(...group.roomsByDate.values())
      : 0;
    const rooms = roomsFromDates || group.sequences.size || 1;
    const uniquePrices = [...new Set(group.prices.map((p) => Number(p.toFixed(2))))];
    const rate = uniquePrices.length === 1
      ? uniquePrices[0]
      : (group.total / Math.max(1, rooms * nights));

    return {
      key: group.key,
      roomTypeName: group.roomTypeName,
      ratePlanName: group.ratePlanName,
      rooms,
      nights,
      rate,
      rateIsMixed: uniquePrices.length > 1,
      total: group.total,
      roomNights: group.prices.length,
    };
  });
}

function MetaRow({ icon: Icon, label, value, valueColor, isDark }) {
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
      <Icon sx={{ fontSize: 15, color: ACCENT, mt: '2px', flexShrink: 0 }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="span"
          sx={{
            display: 'block',
            fontSize: '0.65rem',
            fontWeight: 700,
            letterSpacing: 0.35,
            textTransform: 'uppercase',
            color: isDark ? '#94a3b8' : '#64748b',
            lineHeight: 1.2,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: '0.86rem',
            color: valueColor || (isDark ? '#f1f5f9' : '#0f172a'),
            lineHeight: 1.35,
            wordBreak: 'break-word',
          }}
        >
          {value}
        </Typography>
      </Box>
    </Stack>
  );
}

function MemberBookingCard({ booking, index, total, isDark, currency }) {
  const navigate = useNavigate();
  const items = Array.isArray(booking?.items)
    ? booking.items
    : Array.isArray(booking?.booking_items)
      ? booking.booking_items
      : [];
  const rateRows = useMemo(() => aggregateItemsByRoomTypeAndRatePlan(items), [items]);
  const ratesSubtotal = useMemo(
    () => rateRows.reduce((sum, row) => sum + (Number(row.total) || 0), 0),
    [rateRows]
  );
  const payment = getPaymentSummaryData(booking || {});
  const status = booking?.status_display || booking?.status || '—';
  const muted = isDark ? '#94a3b8' : '#64748b';
  const ink = isDark ? '#f1f5f9' : '#0f172a';
  const borderSoft = isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.08);

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        border: `1px solid ${isDark ? alpha(ACCENT, 0.28) : alpha(ACCENT, 0.22)}`,
        background: isDark
          ? `linear-gradient(165deg, ${alpha(ACCENT, 0.08)} 0%, ${alpha('#0b1220', 0.9)} 45%)`
          : `linear-gradient(165deg, ${alpha(ACCENT, 0.07)} 0%, #ffffff 48%)`,
        boxShadow: isDark
          ? `0 16px 40px ${alpha('#000', 0.35)}`
          : `0 14px 36px ${alpha(ACCENT, 0.12)}`,
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          borderBottom: `1px solid ${borderSoft}`,
          background: isDark ? alpha(ACCENT, 0.08) : alpha(ACCENT, 0.06),
        }}
      >
        <Stack spacing={0.25} minWidth={0}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: muted }}
          >
            Member booking {index + 1} / {total}
          </Typography>
          <Typography sx={{ fontWeight: 800, color: ACCENT, fontSize: '1.05rem' }}>
            {booking?.code || '—'}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            label={String(status).replace(/_/g, ' ')}
            sx={{
              fontWeight: 700,
              textTransform: 'capitalize',
              bgcolor: alpha(ACCENT, isDark ? 0.2 : 0.14),
              color: isDark ? '#e2e8f0' : '#042018',
            }}
          />
          <Button
            size="small"
            variant="outlined"
            endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
            onClick={() => navigate(`/booking/detail/${booking.id}/`)}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderColor: alpha(ACCENT, 0.45),
              color: ACCENT,
              '&:hover': { borderColor: ACCENT, bgcolor: alpha(ACCENT, 0.08) },
            }}
          >
            Open details
          </Button>
        </Stack>
      </Box>

      <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr 1fr',
              md: 'repeat(4, minmax(0, 1fr))',
            },
            gap: 1.25,
            mb: 2,
            p: 1.25,
            borderRadius: 2,
            border: `1px solid ${borderSoft}`,
            bgcolor: isDark ? alpha('#fff', 0.02) : alpha('#0f172a', 0.02),
          }}
        >
          <Stack direction="row" spacing={0.75} alignItems="center" minWidth={0}>
            <LoginIcon sx={{ fontSize: 16, color: ACCENT, flexShrink: 0 }} />
            <Box minWidth={0}>
              <Typography variant="caption" sx={{ color: muted, fontWeight: 700 }}>Check-in</Typography>
              <Typography variant="body2" fontWeight={700} noWrap>{formatDate(booking?.check_in_date)}</Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="center" minWidth={0}>
            <LogoutIcon sx={{ fontSize: 16, color: '#f59e0b', flexShrink: 0 }} />
            <Box minWidth={0}>
              <Typography variant="caption" sx={{ color: muted, fontWeight: 700 }}>Check-out</Typography>
              <Typography variant="body2" fontWeight={700} noWrap>{formatDate(booking?.check_out_date)}</Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="center" minWidth={0}>
            <HotelIcon sx={{ fontSize: 16, color: '#6366f1', flexShrink: 0 }} />
            <Box minWidth={0}>
              <Typography variant="caption" sx={{ color: muted, fontWeight: 700 }}>Rooms / Pax</Typography>
              <Typography variant="body2" fontWeight={700} noWrap>
                {booking?.total_rooms ?? booking?.rooms ?? '—'} · {booking?.pax_adults ?? booking?.adults ?? 0}A / {booking?.pax_children ?? booking?.children ?? 0}C
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="center" minWidth={0}>
            <AttachMoneyIcon sx={{ fontSize: 16, color: '#16a34a', flexShrink: 0 }} />
            <Box minWidth={0}>
              <Typography variant="caption" sx={{ color: muted, fontWeight: 700 }}>Amount</Typography>
              <Typography variant="body2" fontWeight={800} color="#16a34a" noWrap>
                {formatMoney(payment.displayTotal ?? payment.totalAmount ?? booking?.total_amount, currency)}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Stack
          direction="row"
          alignItems="baseline"
          justifyContent="space-between"
          spacing={1}
          sx={{ mb: 0.85 }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 800,
              letterSpacing: 0.5,
              textTransform: 'uppercase',
              color: muted,
            }}
          >
            Rooms & rates
          </Typography>
          {rateRows.length > 0 ? (
            <Typography variant="caption" sx={{ color: muted, fontWeight: 600 }}>
              {rateRows.length} rate line{rateRows.length === 1 ? '' : 's'}
            </Typography>
          ) : null}
        </Stack>

        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: 2,
            border: `1px solid ${borderSoft}`,
            bgcolor: isDark ? alpha('#fff', 0.02) : alpha('#0f172a', 0.015),
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 800, color: ink }}>Room type</TableCell>
                <TableCell sx={{ fontWeight: 800, color: ink }}>Rate plan</TableCell>
                <TableCell sx={{ fontWeight: 800, color: ink }} align="center">Rooms</TableCell>
                <TableCell sx={{ fontWeight: 800, color: ink }} align="center">Nights</TableCell>
                <TableCell sx={{ fontWeight: 800, color: ink }} align="right">Rate</TableCell>
                <TableCell sx={{ fontWeight: 800, color: ink }} align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rateRows.length ? (
                <>
                  {rateRows.map((row) => (
                    <TableRow
                      key={row.key}
                      sx={{ '& td': { borderColor: borderSoft, py: 1.1 } }}
                    >
                      <TableCell>
                        <Typography sx={{ fontWeight: 700, color: ink, fontSize: '0.86rem' }}>
                          {row.roomTypeName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.ratePlanName}
                          sx={{
                            height: 22,
                            fontWeight: 700,
                            fontSize: '0.7rem',
                            bgcolor: alpha(ACCENT, isDark ? 0.16 : 0.1),
                            color: isDark ? '#e2e8f0' : '#042018',
                            border: `1px solid ${alpha(ACCENT, 0.28)}`,
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                          {row.rooms}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                          {row.nights}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                          {formatMoney(row.rate, currency)}
                          <Typography
                            component="span"
                            sx={{ display: 'block', fontSize: '0.65rem', fontWeight: 600, color: muted }}
                          >
                            {row.rateIsMixed ? 'avg / night' : '/ night'}
                          </Typography>
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: ink }}>
                          {formatMoney(row.total, currency)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow
                    sx={{
                      bgcolor: isDark ? alpha(ACCENT, 0.08) : alpha(ACCENT, 0.06),
                      '& td': { borderBottom: 0, py: 1.15 },
                    }}
                  >
                    <TableCell colSpan={5}>
                      <Typography sx={{ fontWeight: 800, color: muted, fontSize: '0.78rem', letterSpacing: 0.3 }}>
                        Subtotal
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography sx={{ fontWeight: 900, color: ACCENT, fontVariantNumeric: 'tabular-nums' }}>
                        {formatMoney(ratesSubtotal, currency)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </>
              ) : (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary">No room rates</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          justifyContent="flex-end"
          sx={{ mt: 1.5 }}
        >
          <Chip
            size="small"
            label={`Paid ${formatMoney(payment.paidAmount ?? 0, currency)}`}
            sx={{ fontWeight: 700, bgcolor: alpha('#16a34a', 0.12), color: '#16a34a' }}
          />
          <Chip
            size="small"
            label={`Due ${formatMoney(payment.remainingAmount ?? 0, currency)}`}
            sx={{ fontWeight: 700, bgcolor: alpha('#f59e0b', 0.14), color: '#b45309' }}
          />
        </Stack>
      </CardContent>
    </Card>
  );
}

const GroupBookingDetailPage = () => {
  const { groupBookingId } = useParams();
  const theme = useTheme();
  const themeMode = theme.palette.mode === 'dark' ? 'dark' : 'light';
  const isDark = themeMode === 'dark';
  const { isCompact } = useMobileView();

  const normalizedId = useMemo(() => {
    const n = Number(groupBookingId);
    return Number.isInteger(n) && n > 0 ? n : null;
  }, [groupBookingId]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [group, setGroup] = useState(null);
  const [docLoading, setDocLoading] = useState(null);
  const [docNotice, setDocNotice] = useState(null);

  const load = useCallback(async () => {
    if (!normalizedId) {
      setError('Invalid group booking ID');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await getGroupBookingDetails(normalizedId);
      setGroup(data);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load group booking');
      setGroup(null);
    } finally {
      setLoading(false);
    }
  }, [normalizedId]);

  useEffect(() => {
    load();
  }, [load]);

  const members = Array.isArray(group?.member_bookings) ? group.member_bookings : [];
  const currency = group?.currency || 'INR';
  const documentBookingId = useMemo(() => {
    const id = Number(group?.invoice_booking_id || members[0]?.id);
    return Number.isInteger(id) && id > 0 ? id : null;
  }, [group?.invoice_booking_id, members]);

  const handleDocNotice = useCallback(
    (notice) => {
      if (!notice) return;
      setDocNotice(notice);
      if (notice.refreshedInvoice) {
        load();
      }
    },
    [load],
  );

  return (
    <Box sx={getDashboardPageShellSx(themeMode)}>
      <DashboardHeader
        title="Group Booking"
        forceDark={isDark}
        backFallbackPath="/booking/list/"
        navLinks={[
          { url: '/booking/list/', label: 'Booking List' },
          { url: '/booking/reservation/', label: 'Reservation' },
        ]}
      />

      <Box sx={{ px: { xs: 1.25, md: 2 }, pb: 3, pt: 1 }}>
        {loading ? (
          <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 280 }}>
            <CircularProgress sx={{ color: ACCENT }} />
          </Box>
        ) : error && !group ? (
          <Alert severity="error">{error}</Alert>
        ) : group ? (
          <>
            {error ? (
              <Alert severity="warning" sx={{ mb: 1.25 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            ) : null}
            {docNotice?.message ? (
              <Alert
                severity={docNotice.severity || 'info'}
                sx={{ mb: 1.25 }}
                onClose={() => setDocNotice(null)}
              >
                {docNotice.message}
              </Alert>
            ) : null}

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.25}
              alignItems="stretch"
              sx={{ mb: 2 }}
            >
              <Paper
                elevation={0}
                sx={{
                  flex: '1 1 auto',
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  p: { xs: 1.5, md: 1.75 },
                  borderRadius: 2.5,
                  border: `1px solid ${isDark ? alpha(ACCENT, 0.28) : alpha(ACCENT, 0.2)}`,
                  background: isDark
                    ? `linear-gradient(145deg, ${alpha(ACCENT, 0.12)} 0%, ${alpha('#0b1220', 0.94)} 50%)`
                    : `linear-gradient(145deg, ${alpha(ACCENT, 0.1)} 0%, #fff 52%)`,
                }}
              >
                  <Stack direction="row" spacing={1.1} alignItems="flex-start" sx={{ mb: 1.25 }}>
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        flexShrink: 0,
                        borderRadius: '12px',
                        display: 'grid',
                        placeItems: 'center',
                        background: 'linear-gradient(135deg, #58f2ce 0%, #3ab896 100%)',
                      }}
                    >
                      <GroupsIcon sx={{ color: '#042018', fontSize: 20 }} />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Stack
                        direction="row"
                        spacing={0.75}
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                        sx={{ mb: 0.35 }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 800,
                            letterSpacing: 0.55,
                            textTransform: 'uppercase',
                            color: isDark ? '#94a3b8' : '#64748b',
                          }}
                        >
                          Group booking
                        </Typography>
                        <Chip
                          size="small"
                          label={(group.status_display || group.status || 'active').replace(/_/g, ' ')}
                          sx={{
                            height: 20,
                            fontWeight: 800,
                            fontSize: '0.68rem',
                            textTransform: 'capitalize',
                            bgcolor: alpha(ACCENT, 0.2),
                            color: isDark ? '#e2e8f0' : '#042018',
                            '& .MuiChip-label': { px: 0.85 },
                          }}
                        />
                        <Chip
                          size="small"
                          label={`${members.length || group.booking_count || 0} members`}
                          sx={{
                            height: 20,
                            fontWeight: 700,
                            fontSize: '0.68rem',
                            '& .MuiChip-label': { px: 0.85 },
                          }}
                        />
                        <Chip
                          size="small"
                          label={group.source_display || group.source || 'Reservation'}
                          sx={{
                            height: 20,
                            fontWeight: 700,
                            fontSize: '0.68rem',
                            '& .MuiChip-label': { px: 0.85 },
                          }}
                        />
                        {group.invoice_number ? (
                          <Chip
                            size="small"
                            label={group.invoice_number}
                            sx={{
                              height: 20,
                              fontWeight: 800,
                              fontSize: '0.68rem',
                              bgcolor: isDark ? 'rgba(148,163,184,0.16)' : 'rgba(15,23,42,0.06)',
                              '& .MuiChip-label': { px: 0.85 },
                            }}
                          />
                        ) : null}
                      </Stack>
                      <Typography
                        sx={{
                          fontWeight: 900,
                          fontSize: isCompact ? '1.1rem' : '1.28rem',
                          color: ACCENT,
                          letterSpacing: 0.15,
                          lineHeight: 1.15,
                          wordBreak: 'break-word',
                        }}
                      >
                        {group.code || group.group_booking_code}
                      </Typography>
                    </Box>
                  </Stack>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr' },
                      gap: 1.1,
                      mb: 1.25,
                      p: 1.1,
                      borderRadius: 2,
                      border: `1px solid ${isDark ? alpha('#fff', 0.07) : alpha('#0f172a', 0.07)}`,
                      bgcolor: isDark ? alpha('#fff', 0.02) : alpha('#0f172a', 0.02),
                    }}
                  >
                    <MetaRow
                      isDark={isDark}
                      icon={PersonIcon}
                      label="Primary guest"
                      value={group.guest?.full_name || group.guest_name || '—'}
                    />
                    <MetaRow
                      isDark={isDark}
                      icon={PhoneIcon}
                      label="Phone"
                      value={group.guest?.phone || group.guest_phone || '—'}
                    />
                    <MetaRow
                      isDark={isDark}
                      icon={CalendarTodayIcon}
                      label="Stay window"
                      value={`${formatDate(group.earliest_check_in)} → ${formatDate(group.latest_check_out)}`}
                    />
                    <MetaRow
                      isDark={isDark}
                      icon={AttachMoneyIcon}
                      label="Group total"
                      value={formatMoney(group.total_amount, currency)}
                      valueColor="#16a34a"
                    />
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1.1fr 1fr 1fr' },
                      gap: 1.25,
                    }}
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 800,
                          letterSpacing: 0.4,
                          textTransform: 'uppercase',
                          color: isDark ? '#94a3b8' : '#64748b',
                          fontSize: '0.65rem',
                        }}
                      >
                        Contact
                      </Typography>
                      <Stack spacing={0.55} sx={{ mt: 0.55 }}>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <EmailIcon sx={{ fontSize: 14, color: ACCENT }} />
                          <Typography variant="body2" sx={{ fontSize: '0.82rem', wordBreak: 'break-word' }}>
                            {group.guest?.email || group.guest_email || '—'}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <HotelIcon sx={{ fontSize: 14, color: ACCENT }} />
                          <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>
                            {group.total_rooms || 0} rooms · {group.pax_adults || 0}A ·{' '}
                            {group.pax_children || 0}C
                          </Typography>
                        </Stack>
                      </Stack>
                    </Box>

                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 800,
                          letterSpacing: 0.4,
                          textTransform: 'uppercase',
                          color: isDark ? '#94a3b8' : '#64748b',
                          fontSize: '0.65rem',
                        }}
                      >
                        Payments
                      </Typography>
                      <Stack spacing={0.35} sx={{ mt: 0.55 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>
                          Paid <strong>{formatMoney(group.paid_amount, currency)}</strong>
                          {' · '}
                          Due <strong>{formatMoney(group.remaining_amount, currency)}</strong>
                        </Typography>
                        <Chip
                          size="small"
                          label={
                            String(group.payment_status || '').toLowerCase() === 'paid'
                              ? 'Full Payment'
                              : String(group.payment_status || '').toLowerCase() === 'partial'
                                ? 'Partial Payment'
                                : 'Unpaid'
                          }
                          sx={{
                            alignSelf: 'flex-start',
                            height: 20,
                            fontWeight: 800,
                            fontSize: '0.68rem',
                            bgcolor: alpha('#2563eb', 0.12),
                            color: '#1d4ed8',
                            '& .MuiChip-label': { px: 0.85 },
                          }}
                        />
                      </Stack>
                    </Box>

                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 800,
                          letterSpacing: 0.4,
                          textTransform: 'uppercase',
                          color: isDark ? '#94a3b8' : '#64748b',
                          fontSize: '0.65rem',
                        }}
                      >
                        Member codes
                      </Typography>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.55 }}>
                        {(group.member_booking_codes || members.map((m) => m.code)).map((code) => {
                          const memberId = members.find((m) => m.code === code)?.id;
                          return (
                            <Chip
                              key={code}
                              size="small"
                              label={code}
                              component={RouterLink}
                              to={`/booking/detail/${memberId || ''}/`}
                              clickable={Boolean(memberId)}
                              sx={{
                                height: 22,
                                fontWeight: 700,
                                fontSize: '0.68rem',
                                bgcolor: alpha(ACCENT, isDark ? 0.14 : 0.1),
                                '& .MuiChip-label': { px: 0.85 },
                              }}
                            />
                          );
                        })}
                      </Stack>
                    </Box>
                  </Box>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  flex: '0 0 auto',
                  width: { xs: '100%', md: 'fit-content' },
                  maxWidth: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  p: { xs: 1.25, md: 1.35 },
                  borderRadius: 2.5,
                  border: `1px solid ${isDark ? alpha(ACCENT, 0.28) : alpha(ACCENT, 0.2)}`,
                  background: isDark
                    ? `linear-gradient(165deg, ${alpha(ACCENT, 0.1)} 0%, ${alpha('#0b1220', 0.92)} 55%)`
                    : `linear-gradient(165deg, ${alpha(ACCENT, 0.08)} 0%, #ffffff 60%)`,
                }}
              >
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.85 }}>
                  <DescriptionOutlinedIcon sx={{ fontSize: 18, color: ACCENT }} />
                  <Typography sx={{ fontWeight: 900, fontSize: '0.9rem', lineHeight: 1.2 }}>
                    Group documents
                  </Typography>
                </Stack>

                {documentBookingId ? (
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <BookingDocumentsPreviewPanel
                      bookingId={documentBookingId}
                      bookingCode={group.code || group.group_booking_code}
                      invoiceNumber={group.invoice_number || null}
                      guestName={group.guest?.full_name || group.guest_name || null}
                      isDark={isDark}
                      accent={ACCENT}
                      docLoading={docLoading}
                      setDocLoading={setDocLoading}
                      groupDocuments
                      compact
                      voucherLabel="Group voucher"
                      invoiceLabel="Group invoice"
                      onNotice={handleDocNotice}
                    />
                  </Box>
                ) : (
                  <Alert severity="info">No member booking available for documents yet.</Alert>
                )}
              </Paper>
            </Stack>

            <Typography
              sx={{
                mb: 1,
                fontWeight: 900,
                fontSize: '0.98rem',
                color: isDark ? '#f8fafc' : '#0f172a',
              }}
            >
              Member bookings
            </Typography>
            <Stack spacing={2}>
              {members.map((member, index) => (
                <MemberBookingCard
                  key={member.id || member.code || index}
                  booking={member}
                  index={index}
                  total={members.length}
                  isDark={isDark}
                  currency={currency}
                />
              ))}
            </Stack>
          </>
        ) : null}
      </Box>
    </Box>
  );
};

export default GroupBookingDetailPage;
